import React, { useState } from 'react';
import { db } from '../lib/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { Language } from '../types/kundali';
import {
  Mail,
  User,
  Phone,
  MessageSquare,
  Send,
  X,
  CheckCircle2,
  Sparkles,
  Info,
  RefreshCw,
  ShieldCheck
} from 'lucide-react';

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang?: Language;
  initialUserEmail?: string;
  initialUserName?: string;
}

export const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  onClose,
  lang = 'en',
  initialUserEmail = '',
  initialUserName = ''
}) => {
  const [name, setName] = useState(initialUserName);
  const [email, setEmail] = useState(initialUserEmail);
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('Astrology Consultation Inquiry');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successResponse, setSuccessResponse] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError(lang === 'hi' ? 'कृपया नाम, ईमेल और संदेश भरें' : 'Please fill in name, email, and message fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Submit to Backend API endpoint (logs & dispatches)
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          subject: subject.trim(),
          message: message.trim()
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit message');
      }

      // 2. Save document into Firestore 'contactMessages' collection
      try {
        await addDoc(collection(db, 'contactMessages'), {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          subject: subject.trim(),
          message: message.trim(),
          targetRecipient: 'vedangajyotisha108@gmail.com',
          createdAt: new Date().toISOString(),
          status: 'unread'
        });
      } catch (firestoreErr) {
        console.warn('Firestore backup save notice:', firestoreErr);
      }

      setSuccessResponse(data.message);
      setMessage('');
    } catch (err: any) {
      setError(err.message || 'Error submitting contact form');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-astro-text/40 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div 
        className="bg-astro-bg border border-astro-border w-full max-w-lg rounded-3xl p-6 sm:p-8 shadow-2xl relative my-8 text-astro-text"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-astro-secondary-text hover:text-astro-text p-2 rounded-xl bg-astro-card border border-astro-border transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-2xl bg-astro-primary/10 border border-astro-primary/20 flex items-center justify-center text-astro-primary mx-auto mb-3">
            <Mail className="w-6 h-6" />
          </div>
          <h2 className="text-2xl font-bold text-astro-primary font-serif">
            {lang === 'hi' ? 'संपर्क साधें (Contact Form)' : 'Direct Contact & Inquiries'}
          </h2>
          <p className="text-xs text-astro-secondary-text mt-1">
            {lang === 'hi'
              ? 'अपना संदेश भेजें, यह सीधे (vedangajyotisha108@gmail.com) पर पहुँचेगा'
              : 'Your inquiry will be delivered directly to vedangajyotisha108@gmail.com'}
          </p>
        </div>

        {/* Success Toast */}
        {successResponse ? (
          <div className="text-center space-y-4 py-6">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border-2 border-emerald-500/40 text-emerald-600 mx-auto flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-lg font-bold text-astro-text">
              {lang === 'hi' ? 'संदेश सफलतापूर्वक भेजा गया!' : 'Inquiry Sent Successfully!'}
            </h3>
            <p className="text-xs text-astro-secondary-text max-w-sm mx-auto leading-relaxed">
              {successResponse}
            </p>
            <div className="p-3 bg-astro-card rounded-xl border border-astro-border text-[11px] text-astro-primary font-mono">
              Target Recipient: vedangajyotisha108@gmail.com
            </div>
            <button
              onClick={() => {
                setSuccessResponse(null);
                onClose();
              }}
              className="w-full py-3 bg-astro-primary text-white font-bold rounded-xl text-xs hover:bg-astro-primary/90 transition-all"
            >
              {lang === 'hi' ? 'बंद करें' : 'Done & Return'}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-700 text-xs flex items-center gap-2">
                <Info className="w-4 h-4 shrink-0 text-red-600" />
                <span>{error}</span>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'आपका नाम *' : 'Your Name *'}
                </label>
                <div className="relative">
                  <User className="w-4 h-4 absolute left-3 top-3 text-astro-secondary-text" />
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Rahul Sharma"
                    className="w-full pl-9 pr-3 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'आपका ईमेल *' : 'Your Email *'}
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-3 text-astro-secondary-text" />
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@gmail.com"
                    className="w-full pl-9 pr-3 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none font-mono"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'फ़ोन / व्हाट्सएप नंबर' : 'Phone / WhatsApp'}
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-3 text-astro-secondary-text" />
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="w-full pl-9 pr-3 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                  {lang === 'hi' ? 'विषय' : 'Inquiry Topic'}
                </label>
                <select
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2.5 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
                >
                  <option value="Astrology Consultation Inquiry">Astrology Consultation Inquiry</option>
                  <option value="Kundali Matching & Compatibility">Kundali Matching & Marriage</option>
                  <option value="Gemstone & Remedial Guidance">Gemstone & Remedial Guidance</option>
                  <option value="General Question / Feedback">General Question / Feedback</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-astro-secondary-text mb-1">
                {lang === 'hi' ? 'संदेश विवरण *' : 'Message Details *'}
              </label>
              <textarea
                required
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={
                  lang === 'hi'
                    ? 'कृपया अपने प्रश्न, जन्म विवरण या टिप्पणी यहाँ लिखें...'
                    : 'Describe your question, birth detail inquiries, or specific astrological requests...'
                }
                className="w-full p-3 bg-astro-card border border-astro-border rounded-xl text-xs text-astro-text focus:border-astro-primary focus:outline-none"
              />
            </div>

            <div className="p-3 bg-astro-card rounded-xl border border-astro-border flex items-center gap-2 text-[11px] text-astro-secondary-text">
              <ShieldCheck className="w-4 h-4 text-astro-primary shrink-0" />
              <span>
                {lang === 'hi'
                  ? 'यह फॉर्म सीधे vedangajyotisha108@gmail.com पर आपकी जानकारी सुरक्षित पहुँचाता है।'
                  : 'Delivers directly to vedangajyotisha108@gmail.com'}
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-astro-primary text-white font-bold rounded-xl hover:bg-astro-primary/90 transition-all flex items-center justify-center gap-2 text-xs shadow-lg disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4 text-astro-accent" />
              )}
              {loading
                ? (lang === 'hi' ? 'भेजा जा रहा है...' : 'Sending Message...')
                : (lang === 'hi' ? 'संदेश भेजें' : 'Send Message to vedangajyotisha108@gmail.com')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
